// Phase 04 seam hooks for the Comparison scatter (04i-b). Each is a thin useMemo over
// the pure data/comparison.ts layer + the snapshot — the same "hook over a pure lookup" shape
// as useFilterResults. The compute reads the full player population (not the 02g
// useComparisonPopulation, which the faithful widget deliberately never uses).
//
// `useComparisonBoard` is what the widget reads: the snapshot path is the hooks below, unchanged;
// with ?live=1 the same board comes from the shared group (`useLiveGroupFile` — her league's
// Stats file), plotted by Ben's own computeComparisonPoints (`live/comparison.ts`).

import { chipIsOn } from "../engines/stats-filter-kit.js";
import { leagueOptionInfo } from "../modules/CountryFlag.js";
import type { OptionInfo } from "../modules/FilterPopovers.js";
import {
  chipOptionsOfGroups,
  filesOfGroups,
  groupsKey,
  playersOfGroups,
  refPlayerOf,
  useLiveLeagueGroups,
} from "./useLeagueGroups.js";
import { chipsWithData } from "../live/stats-files.js";
import { useMemo } from "react";
import { readSnapshot } from "../data/snapshot.js";
import { getMetricCategories } from "../data/stats.js";
import { playerIdOf, type PlayerId } from "../data/players.js";
import {
  comparisonSeasonCodes,
  comparisonMetricGroups,
  buildComparisonMetricLookup,
  computeComparisonPoints,
  COMPARISON_RANGE_FILTERS,
  type ComparisonMetricEntry,
  type ComparisonPoint,
} from "../data/comparison.js";
import type { ActiveFilters, CategoricalActive, KitPlayer, RangeActive, RangeFilterConfig } from "../engines/stats-filter-kit.js";
import { useFilterOptions } from "./useFilterOptions.js";
import { LIVE } from "../live/flag.js";
import { distinctPlayers } from "../live/group-players.js";
import { comparisonMetricKeys, labelPositionRows, LIVE_COMPARISON_CHIPS, LIVE_COMPARISON_RANGE_FILTERS } from "../live/comparison.js";
import { MINUTES_CHIP_DEFAULT } from "../live/stats-files.js";

/** Season codes with recorded stats anywhere, newest-first. */
export function useComparisonSeasonCodes(): string[] {
  return useMemo(() => comparisonSeasonCodes(readSnapshot().players), []);
}

/** The 162 metrics grouped by category, for the X/Y/Size/Colour pickers. */
export function useComparisonMetricGroups(): { group: string; items: { value: string; label: string }[] }[] {
  return useMemo(() => comparisonMetricGroups(getMetricCategories()), []);
}

/** key → {metric, category} lookup for resolving the selected axis keys. */
export function useComparisonMetricLookup(): Map<string, ComparisonMetricEntry> {
  return useMemo(() => buildComparisonMetricLookup(getMetricCategories()), []);
}

export interface UseComparisonPointsParams {
  active: ActiveFilters;
  refPlayer: KitPlayer;
  seasonCode: string;
  xKey: string;
  yKey: string;
  sizeKey: string | null;
  colorKey: string | null;
  rangeFilters: RangeFilterConfig;
}

/** The plotted points for the current axes/season/filters. `active` is depended on BY REFERENCE
 *  (like useRanking/useFilterResults) — the chip-bar (04i-c) hands a fresh clone per version so
 *  an in-place chip edit reaches this memo. */
export function useComparisonPoints(params: UseComparisonPointsParams): ComparisonPoint[] {
  const { active, refPlayer, seasonCode, xKey, yKey, sizeKey, colorKey, rangeFilters } = params;
  return useMemo(
    () => computeComparisonPoints({ players: readSnapshot().players, active, refPlayer, seasonCode, xKey, yKey, sizeKey, colorKey, rangeFilters }),
    [active, refPlayer, seasonCode, xKey, yKey, sizeKey, colorKey, rangeFilters],
  );
}

// --- the widget's board (snapshot or live) ---------------------------------------------

export interface ComparisonBoardParams {
  /** The player the group is taken from (live: her league; snapshot: unused — Ben's chips seed
   *  from the first player). */
  sourcePlayer: PlayerId | null;
  /** Live only: Per 90 (default) or Total (Ben, Q-016). */
  mode?: "per90" | "total";
  /** Live: the user removed the League chip (✕) — empty until a league is picked. */
  leagueCleared?: boolean;
  /** The season picked (`_cmpSeason`); null or one without data → the default. */
  season: string | null;
  active: ActiveFilters;
  xKey: string;
  yKey: string;
  sizeKey: string | null;
  colorKey: string | null;
}

/** What the chip bar needs (`FilterChipBar`'s props). The optional ones are live only. */
export interface ComparisonChips {
  /** Live only: each value's heading + icon in its list (the League chip: country + flag). */
  optionInfo?: Record<string, (value: string) => OptionInfo | undefined>;
  options: Record<string, string[]>;
  refPlayer: KitPlayer;
  rangeFilters: RangeFilterConfig;
  addMenuKeys?: readonly string[];
  newChipValue?: (key: string) => CategoricalActive | RangeActive | undefined;
}

export interface ComparisonBoard {
  /** Live only: the League chip's default — the canvas player's league (Ben, 2026-10-16). */
  defaultLeague?: string;
  seasonCodes: string[];
  /** The season shown. */
  season: string;
  points: ComparisonPoint[];
  /** "N players plotted" (distinct players: a Position chip can plot one player twice). */
  count: number;
  /** The readout's words. */
  label: string;
  /** Live with no group (her league's season isn't built yet): why, shown in place of the chart. */
  noGroup: string | null;
  chips: ComparisonChips;
  /** The plotted players' gender (a league is one gender) — a dragged dot notes it, so a live
   *  Stats tile can take her (T2-14b). Null = no file shown (no dots either). */
  womens: boolean | null;
}

function useSnapshotComparisonBoard({ season, active, xKey, yKey, sizeKey, colorKey }: ComparisonBoardParams): ComparisonBoard {
  const seasonCodes = useComparisonSeasonCodes();
  const shown = season && seasonCodes.includes(season) ? season : (seasonCodes[0] ?? "");
  // A purely-internal seed for a categorical chip's default (never surfaced); the first player,
  // exactly like renderers-comparison.js:677.
  const refPlayer = useMemo<KitPlayer>(() => {
    const first = Object.values(readSnapshot().players)[0];
    return (first ?? {}) as unknown as KitPlayer;
  }, []);
  const options = useFilterOptions();
  const points = useComparisonPoints({
    active,
    refPlayer,
    seasonCode: shown,
    xKey,
    yKey,
    sizeKey,
    colorKey,
    rangeFilters: COMPARISON_RANGE_FILTERS,
  });
  return {
    seasonCodes,
    season: shown,
    points,
    count: points.length,
    label: "players plotted",
    noGroup: null,
    chips: { options, refPlayer, rangeFilters: COMPARISON_RANGE_FILTERS },
    // The snapshot holds women's leagues only (and the snapshot site has no gender rule).
    womens: true,
  };
}

// A League chip added from "+ Add filter" starts with nothing ticked (Ben, 2026-10-16: not the
// player's league, which a first tick would then add to). Minutes starts at 150+ (rules.md).
const liveNewChipValue = (key: string) =>
  key === "league" ? [] : key === "minutes" ? { ...MINUTES_CHIP_DEFAULT } : undefined;

function useLiveComparisonBoard({
  sourcePlayer,
  season,
  active,
  xKey,
  yKey,
  sizeKey,
  colorKey,
  leagueCleared,
  mode = "per90",
}: ComparisonBoardParams): ComparisonBoard {
  // Live always has one: the module falls back to the snapshot's first player (a real DB id).
  if (sourcePlayer === null) throw new Error("A live Comparison needs a player to take its league from");
  // The League chip's leagues (useLeagueGroups: the canvas player's by default, tick others) —
  // Ben's prototype had `League: FAWSL` here (2026-10-16).
  const lg = useLiveLeagueGroups(sourcePlayer, season, active.league as CategoricalActive | undefined, leagueCleared);
  const key = groupsKey(lg);
  const byPosition = chipIsOn(active.position);

  const refPlayer = useMemo<KitPlayer>(() => refPlayerOf(lg, sourcePlayer), [lg.home, sourcePlayer]);
  const options = useMemo(() => chipOptionsOfGroups(lg), [key, lg.leagueOptions]);

  const keys = useMemo(() => comparisonMetricKeys([xKey, yKey, sizeKey, colorKey]), [xKey, yKey, sizeKey, colorKey]);
  const players = useMemo(() => {
    if (lg.problem !== null) return {};
    if (!byPosition) return playersOfGroups(lg, "total", keys);
    return labelPositionRows(playersOfGroups(lg, "position", keys));
  }, [lg.problem, key, byPosition, keys, lg.shown]);
  const points = useMemo(
    () =>
      computeComparisonPoints({
        players,
        active,
        refPlayer,
        seasonCode: lg.shown,
        xKey,
        yKey,
        sizeKey,
        colorKey,
        rangeFilters: LIVE_COMPARISON_RANGE_FILTERS,
        mode,
      }),
    [players, active, refPlayer, lg.shown, xKey, yKey, sizeKey, colorKey, mode],
  );

  const chips: ComparisonChips = {
    options,
    refPlayer,
    rangeFilters: LIVE_COMPARISON_RANGE_FILTERS,
    // Q-013: only the chips at least half the players (with minutes) have data for.
    addMenuKeys: chipsWithData(LIVE_COMPARISON_CHIPS, filesOfGroups(lg)),
    newChipValue: liveNewChipValue,
    optionInfo: { league: leagueOptionInfo(lg.leagues) },
  };
  const base = { seasonCodes: lg.seasonCodes, season: lg.shown, chips, defaultLeague: lg.defaultLeague };
  if (lg.problem !== null) {
    return { ...base, points: [], count: 0, label: "players plotted", noGroup: lg.cleared ? "Pick a league" : lg.problem, womens: null };
  }
  return {
    ...base,
    points,
    count: distinctPlayers(points.map((p) => ({ playerId: playerIdOf(p.player) }))),
    // The League chip says which league(s); a picked league with no file this season is named.
    label: lg.missingNote ? `players plotted · ${lg.missingNote}` : "players plotted",
    noGroup: null,
    womens: lg.womens,
  };
}

/** The Comparison widget's board: the snapshot's, or with ?live=1 her league's Stats file. */
export const useComparisonBoard: (p: ComparisonBoardParams) => ComparisonBoard = LIVE
  ? useLiveComparisonBoard
  : useSnapshotComparisonBoard;
